"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { getPiecePreview, type PiecePreview } from "@/lib/api/api-client";
import { getPianoAuditionSynth } from "@/lib/audio/piano-audition-synth";
import { MidiAuditionPlayer } from "@/lib/midi/midi-audition-player";
import { useStudio } from "@/contexts/studio-context";

/**
 * Écouter vingt secondes d'un morceau depuis sa fiche.
 *
 * # Pourquoi ça compte plus qu'une image
 *
 * Sur un catalogue de titres classiques, « est-ce que c'est l'air que j'ai en
 * tête ? » est la seule question qui décide — et aucune image n'y répond. Un
 * titre non plus, d'ailleurs : le même thème s'appelle « Inno alla Gioia »,
 * « Himne a l'alegria » ou « Hymne à la joie » selon la source.
 *
 * # Ce qu'on ne fait surtout pas
 *
 * On ne passe **ni par le transport, ni par une `Lane`, ni par le modèle
 * `Piano`**. Ce sont les trois seules sources qui sortent le ticker Pixi de
 * son parking (`lib/studio/view/studio-view.ts`), et la bibliothèque est une
 * page où le piano-roll n'est même pas visible : le réveiller ferait tourner
 * un canvas invisible à 60 images par seconde.
 *
 * `MidiAuditionPlayer` est autonome par construction — son en-tête le dit,
 * « standalone (no reuse of Lane / Scheduler / Transport) » — et il apporte
 * déjà l'ordonnanceur à lookahead, l'annulation par génération et le clamp de
 * fenêtre dont on a besoin. On le rebranche, on ne le réécrit pas.
 *
 * Le contre-modèle est le jingle du Studio, qui passe par le chemin complet :
 * il a laissé une cicatrice dans le code, `Studio.resetTimeline()` n'existant
 * que pour réparer la dérive de tempo qu'il causait.
 *
 * # Le son est celui du piano-roll
 *
 * `getPianoAuditionSynth` sert le même Salamander que le studio, et non la
 * banque General MIDI de la modale d'import : le catalogue est du piano solo,
 * donc il n'y a rien à deviner, et un extrait qui ne sonne pas comme le morceau
 * qu'il annonce fait douter de l'écran où l'on choisit. Voir
 * `lib/audio/piano-audition-synth.ts`.
 *
 * # L'extrait est chargé au premier clic
 *
 * Pas au montage : la plupart des visiteurs d'une fiche n'écouteront pas, et
 * leur faire payer la requête serait la même erreur que le préchargement du
 * jingle sur la bibliothèque.
 */

export type PreviewStatus = "unavailable" | "idle" | "loading" | "playing";

type UsePiecePreviewResult = {
  status: PreviewStatus;
  /** Avancement dans l'extrait, `0..1`. Vaut 0 hors lecture. */
  progress: number;
  /** Durée de l'extrait en ms, `null` tant qu'il n'est pas chargé. */
  durationMs: number | null;
  /** Bascule lecture / arrêt. À appeler **directement** depuis un `onClick`. */
  toggle: () => void;
  stop: () => void;
};

export function usePiecePreview(pieceId: string): UsePiecePreviewResult {
  const { studio } = useStudio();

  const [status, setStatus] = useState<PreviewStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [durationMs, setDurationMs] = useState<number | null>(null);

  const playerRef = useRef<MidiAuditionPlayer | null>(null);
  const previewRef = useRef<PiecePreview | null>(null);
  const frameRef = useRef<number | null>(null);
  /**
   * Garde d'obsolescence. Un clic sur « écouter » pendant qu'une requête est
   * en vol, ou une navigation vers une autre fiche, doit empêcher la réponse
   * tardive de démarrer une lecture que plus personne n'attend.
   */
  const runRef = useRef(0);

  const stopSweep = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    runRef.current += 1;
    stopSweep();
    playerRef.current?.stop();
    setStatus("idle");
    setProgress(0);
  }, [stopSweep]);

  // Changer de morceau, ou quitter la fiche, coupe le son. Sans ça l'extrait
  // continue de jouer par-dessus la fiche suivante.
  useEffect(() => {
    return () => {
      runRef.current += 1;
      stopSweep();
      playerRef.current?.dispose();
      playerRef.current = null;
      previewRef.current = null;
    };
  }, [pieceId, stopSweep]);

  const sweep = useCallback(
    (startedAtMs: number, totalMs: number, runId: number) => {
      const tick = () => {
        if (runRef.current !== runId) {
          return;
        }

        const elapsed = performance.now() - startedAtMs;

        if (elapsed >= totalMs) {
          stopSweep();
          setProgress(0);
          setStatus("idle");

          return;
        }

        setProgress(elapsed / totalMs);
        frameRef.current = requestAnimationFrame(tick);
      };

      frameRef.current = requestAnimationFrame(tick);
    },
    [stopSweep],
  );

  const toggle = useCallback(() => {
    if (status === "playing") {
      stop();

      return;
    }

    if (status === "loading" || !studio) {
      return;
    }

    // `resume()` doit partir **dans** le gestionnaire de clic, pas après un
    // `await` : le contexte audio est encore suspendu sur la bibliothèque,
    // puisque le geste qui le débloque d'habitude — le carton d'entrée dans
    // le studio — vit dans un conteneur masqué. Ce clic-ci *est* le geste.
    const ctx = studio.getAudioContext();
    void ctx.resume();

    const runId = ++runRef.current;
    setStatus("loading");

    void (async () => {
      try {
        let preview = previewRef.current;

        if (preview === null) {
          preview = await getPiecePreview(pieceId);
          previewRef.current = preview;
        }

        if (runRef.current !== runId || preview.notes.length === 0) {
          return;
        }

        if (playerRef.current === null) {
          playerRef.current = new MidiAuditionPlayer(ctx, getPianoAuditionSynth(ctx), [
            {
              index: 0,
              // Ignoré par le banc de piano, qui n'a qu'un instrument. Le champ
              // appartient au format d'audition, partagé avec la modale
              // d'import, où il compte.
              gmProgram: 0,
              isDrumChannel: false,
              // Vide : ces compartiments alimentent la frise de densité de la
              // modale d'import, que l'aperçu n'affiche pas. Le lecteur ne les
              // lit jamais de lui-même.
              densityBuckets: [],
              notes: preview.notes.map((note) => ({
                midi: note.m,
                startMs: note.t,
                durationMs: note.d,
                velocity: note.v,
              })),
            },
          ]);
        }

        await playerRef.current.whenReady();

        if (runRef.current !== runId) {
          return;
        }

        playerRef.current.playAll(0, preview.durationMs / 1000);
        setDurationMs(preview.durationMs);
        setStatus("playing");
        sweep(performance.now(), preview.durationMs, runId);
      } catch {
        if (runRef.current !== runId) {
          return;
        }

        // On ne montre pas de message : un aperçu est un bonus, et une pièce
        // sans extrait jouable doit simplement ne pas proposer le bouton.
        setStatus("unavailable");
      }
    })();
  }, [status, studio, pieceId, stop, sweep]);

  return { status, progress, durationMs, toggle, stop };
}
